"use client"

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { UserTemplate } from '@/supabase/types'
import { useLanguage } from "@/contexts/language-context"
import { useCommunicationOptions } from "@/hooks/use-communication-options"

interface TemplateFormProps {
  template?: UserTemplate
  onCancel: () => void
  onSaved: (template: UserTemplate) => void
  onDelete?: (id: string) => void
}

export function TemplateForm({ template, onCancel, onSaved, onDelete }: TemplateFormProps) {
  const { t } = useLanguage()
  const { scenarios, targets, tones } = useCommunicationOptions()
  const [name, setName] = useState(template?.name || '')
  const [nameError, setNameError] = useState('')
  const [scenario, setScenario] = useState(template?.scenario || 'none')
  const [target, setTarget] = useState(template?.target || 'none')
  const [tone, setTone] = useState(template?.tone || 'none')
  const [content, setContent] = useState(template?.content || '')
  const [isSaving, setIsSaving] = useState(false)



  const handleSubmit = async () => {
    if (!name.trim()) {
      setNameError(t('templates.nameRequired'))
      return
    }
    setIsSaving(true)
    try {
      const method = template ? 'PUT' : 'POST'
      const url = template ? `/api/templates/${template.id}` : '/api/templates'
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ name, scenario, target, tone, content })
      })

      if (!response.ok) {
  throw new Error('Failed to save template')
      }

      const data = await response.json()
      onSaved(data.template)
    } catch (err) {
      console.error(err)
  alert(t('templates.saveFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!template || !template.id) return
    if (!confirm(t('templates.deleteConfirm'))) return
    setIsSaving(true)
    try {
      const res = await fetch(`/api/templates/${template.id}`, {
        method: 'DELETE',
        credentials: 'include'
      })
      if (!res.ok) throw new Error('Failed to delete')
      onDelete && onDelete(template.id)
    } catch (err) {
      console.error(err)
      alert(t('templates.deleteFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="space-y-4 bg-zinc-color p-6 rounded-lg">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <label className="text-sm font-medium">{t('templates.name')}</label>
          <Input
            className={cn(
              'bg-card-color',
              nameError &&
                'border-alert'
            )}
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (nameError) setNameError('')
            }}
          />
          {nameError && <p className="text-sm text-alert font-medium">{nameError}</p>}
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">{t('templates.scenario')}</label>
          <Select value={scenario || 'none'} onValueChange={setScenario}>
            <SelectTrigger className="w-full bg-card-color dark:text-gray-50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {scenarios.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">{t('templates.target')}</label>
          <Select value={target || 'none'} onValueChange={setTarget}>
            <SelectTrigger className="w-full bg-card-color dark:text-gray-50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {targets.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-medium">{t('templates.tone')}</label>
          <Select value={tone || 'none'} onValueChange={setTone}>
            <SelectTrigger className="w-full bg-card-color dark:text-gray-50">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {tones.map(t => (
                <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
  <label className="text-sm font-medium">{t('templates.description')}</label>
        <Textarea className="bg-card-color dark:text-gray-50" value={content} onChange={(e) => setContent(e.target.value)} rows={5} />
      </div>
      <div className="flex justify-between gap-2">
        {template && template.id && (
          <Button variant="destructive" onClick={handleDelete} disabled={isSaving} className="bg-botton-color">
            {t('templates.delete')}
          </Button>
        )}
        <div className="ml-auto flex gap-2">
          <Button variant="outline" onClick={onCancel} className="bg-botton-color">{t('common.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={isSaving} className="bg-botton-color">
            {isSaving ? t('templates.saving') : t('templates.save')}
          </Button>
        </div>
      </div>
    </div>
  )
}
